import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

const NEWS_BFF_PATHS = {
  list: '/api/v4/bit/web/wikibit/queryNewIndexArticleList',
  search: '/api/v4/bit/web/wikibit/searchall',
  home: '/api/v4/bit/web/home/news',
} as const

/** 新闻所属 tab 类型对应的 SEO 详情页路径，未识别类型保持默认新闻路径。 */
export const NEWS_DETAIL_TAB_PATHS: Record<number, string> = {
  0: '/news',
  1: '/blog',
  2: '/industry',
  3: '/exchangenews',
  4: '/press',
  5: '/research',
}

/** 根据接口下发的所属 tab 类型和 slug 生成新闻详情规范路径。 */
export const getNewsDetailPath = (
  belongTabType: number | string | null | undefined,
  slug: string | null | undefined,
) => {
  const parsedBelongTabType = Number(belongTabType)
  const normalizedBelongTabType = Number.isInteger(parsedBelongTabType) ? parsedBelongTabType : 0
  const normalizedSlug = slug?.trim().replace(/^\/+|\/+$/g, '') || ''
  const categoryPath = NEWS_DETAIL_TAB_PATHS[normalizedBelongTabType] || NEWS_DETAIL_TAB_PATHS[0]
  const detailPath = normalizedSlug ? `${categoryPath}/${normalizedSlug}` : categoryPath

  return detailPath
}

/** 归一化接口返回的新闻所属 tab 类型，未知类型回退到最新新闻 tab。 */
export const getNewsBelongTabType = (article: { belongTabType?: number | string | null }) => {
  const parsedBelongTabType = Number(article.belongTabType)

  return Number.isInteger(parsedBelongTabType) && parsedBelongTabType >= 0 && parsedBelongTabType <= 5
    ? parsedBelongTabType
    : 0
}

export type NewsCategoryItem = {
  name?: string
  code?: string
  isEn?: boolean
  isdefault?: boolean
  isDefault?: boolean
  sort?: number
}

export type NewsListItem = {
  code?: string
  artcode?: string
  title?: string
  content?: string
  imgurl?: string
  coverImg?: string
  summary?: string
  showtime?: string
  wiki_timestamp?: number
  belongTabType?: number | string | null
  categoryid?: string
  CategoryId?: string
  tags?: Array<{ tagname?: string; tagcolor?: string }>
  urlPath?: string | null
  traderCode?: string | null
  name?: string | null
  ico?: string | null
  icon?: string | null
  showname?: string | null
}

export type NewsListResult = {
  total?: number
  items?: NewsListItem[]
  categorycode?: string | null
  refreshtotal?: number
}

export type ThirdPartyArticleListParams = {
  code: string
  pageIndex?: number
  pageSize?: number
  isHoneycomb?: boolean
}

export type NewsHomeResult = {
  categories?: NewsCategoryItem[]
  categoryList?: NewsCategoryItem[]
  newCategory?: NewsCategoryItem[]
  hot?: NewsListResult | NewsListItem[]
  hotNews?: NewsListResult | NewsListItem[]
  hotArticles?: NewsListResult | NewsListItem[]
  hotArticle?: NewsListResult | NewsListItem[]
}

type NewsResponse<T> = {
  result?: T
  data?: T | { result?: T }
  items?: NewsListItem[]
  total?: number
  code?: number
  succeed?: boolean
  message?: string
}

export type NewsCategoryParams = {
  languageId?: string
  countryId?: string
}

export type NewsListParams = NewsCategoryParams & {
  categoryCode?: string
  lasttime?: string
  pageIndex?: number
  pageSize?: number
}

export type NewsSearchParams = NewsCategoryParams & {
  keyword?: string
  pageIndex?: number
  pageSize?: number
}

const requestNews = async <T>(url: string, params: Record<string, unknown>) => {
  const api = useEnvApi()
  const config = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url,
    method: 'GET' as const,
  }

  return import.meta.server
    ? await useServerFetch<NewsResponse<T>>({ ...config, query: params })
    : await request<unknown, NewsResponse<T>>({ ...config, params })
}

const unwrapNewsResult = <T>(response: NewsResponse<T> | null | undefined): T | null => {
  if (!response) return null
  if (response.result !== undefined) return response.result
  if (response.items || response.total !== undefined) return response as T
  if (response.data && typeof response.data === 'object' && 'result' in response.data) {
    return response.data.result as T
  }
  // 关联新闻网关的响应为 data.data.result，统一解包为分页结果。
  if (response.data && typeof response.data === 'object' && 'data' in response.data) {
    const nestedData = response.data.data
    if (nestedData && typeof nestedData === 'object' && 'result' in nestedData) {
      return nestedData.result as T
    }
  }
  return (response.data as T | undefined) ?? null
}

/** 获取新闻首页聚合数据，包含分类和热点资讯。 */
export const getNewsHome = async (params: NewsCategoryParams = {}) => {
  const languageCode = params.languageId || 'zh-cn'
  const countryCode = params.countryId || '156'
  const response = await requestNews<NewsHomeResult>(NEWS_BFF_PATHS.home, {
    languageCode: languageCode.toLowerCase(),
    countryCode,
  })
  const result = unwrapNewsResult(response) || {}
  const hotResult = result.hot || result.hotNews || result.hotArticles || result.hotArticle || []

  return {
    categories: result.categories || result.categoryList || result.newCategory || [],
    hot: Array.isArray(hotResult) ? { total: hotResult.length, items: hotResult } : hotResult,
  }
}

/** 获取分类新闻分页列表。 */
export const getNewsArticleList = async (params: NewsListParams = {}) => {
  const response = await requestNews<NewsListResult>(NEWS_BFF_PATHS.list, {
    code: params.categoryCode,
    lasttime: params.lasttime,
    languageCode: (params.languageId || 'zh-cn').toLowerCase(),
    countryCode: params.countryId || '156',
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
  })
  return unwrapNewsResult(response) || { total: 0, items: [] }
}

/** 获取交易所关联的第三方新闻分页数据。 */
export const getThirdPartyArticleList = async (params: ThirdPartyArticleListParams): Promise<NewsListResult> => {
  const api = useEnvApi()
  const response = await request<unknown, NewsResponse<NewsListResult>>({
    baseURL: api.v4,
    url: '/api/v4/bit/web/article/getthirdpartybyrelationid',
    method: 'GET',
    params: {
      code: params.code,
      pageIndex: params.pageIndex || 2,
      pageSize: params.pageSize || 10,
      isHoneycomb: params.isHoneycomb ?? false,
    },
  })

  return unwrapNewsResult(response) || { total: 0, items: [] }
}

/** 按关键词搜索新闻，接口参数名为 content。 */
export const searchNewsArticles = async (params: NewsSearchParams = {}) => {
  const response = await requestNews<NewsListResult>(NEWS_BFF_PATHS.search, {
    content: params.keyword || '',
    languageCode: (params.languageId || 'zh-cn').toLowerCase(),
    countryCode: params.countryId || '156',
    pageIndex: params.pageIndex || 1,
    pageSize: params.pageSize || 10,
  })
  return unwrapNewsResult(response) || { total: 0, items: [] }
}

export type NewsDetailResponse = {
  article?: Record<string, unknown> | null
  guessYouLike?: Array<Record<string, unknown>>
}

type NewsDetailWrappedResponse<T> = {
  code?: number
  data?: T | null
  message?: string
  succeed?: boolean
}

export type NewsDetailApiResponse<T> = NewsDetailWrappedResponse<T>

/**
 * 统一 SSR $fetch 与客户端 axios 拦截器可能返回的详情包体。
 * axios 通常已返回 data，但当 BFF 返回非标准包体时仍可能保留外层 data；
 * 在 API 层归一化可保证两端页面拿到相同结构。
 */
const isNewsDetailWrappedResponse = <T>(
  response: T | NewsDetailWrappedResponse<T> | null | undefined,
): response is NewsDetailWrappedResponse<T> => {
  return response !== null && typeof response === 'object' && 'data' in response
}

const unwrapNewsDetailResponse = <T>(response: T | NewsDetailWrappedResponse<T> | null | undefined): T | null => {
  if (isNewsDetailWrappedResponse(response)) {
    return response.data ?? null
  }

  return response ?? null
}

export type NewsDetailStatementResponse = {
  wikicommunity?: {
    detailstatement?: {
      data?: {
        title?: string | null
        content?: string | null
        url?: string | null
      }
    }
  }
}

/** 获取新闻详情，SSR 与客户端分别复用应用统一的请求封装。 */
export const getNewsDetail = async <T = NewsDetailResponse>(slug: string) => {
  const api = useEnvApi()
  const requestConfig = {
    // 服务端走内网 BFF，客户端必须走 HTTPS 网关，避免生产页面触发 Mixed Content。
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/news/detail',
    method: 'GET' as const,
    params: {
      slug,
    },
  }

  if (import.meta.server) {
    const response = await useServerFetch<NewsDetailWrappedResponse<T>>(requestConfig)
    return unwrapNewsDetailResponse(response)
  }

  const response = await request<unknown, T | NewsDetailWrappedResponse<T>>(requestConfig)
  return unwrapNewsDetailResponse(response)
}

/**
 * 获取新闻详情原始响应，用于旧版详情链接校验接口业务状态后再跳转。
 */
export const getNewsDetailResponse = async <T = NewsDetailResponse>(slug: string) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: '/api/v4/bit/web/news/detail',
    method: 'GET' as const,
    params: {
      slug,
    },
  }

  if (import.meta.server) {
    return await useServerFetch<NewsDetailApiResponse<T>>(requestConfig)
  }

  return await request<unknown, NewsDetailApiResponse<T>>(requestConfig)
}

/** 获取新闻详情免责声明，保持聚合接口的 Routes 请求头约定。 */
export const getNewsDetailStatement = async <T = NewsDetailStatementResponse>() => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: `${WIKIBITWEBV1}/invoke-v3/v3`,
    method: 'POST' as const,
    data: {
      detailType: 1,
    },
    headers: {
      Routes: 'wikicommunity$detailstatement',
    },
  }

  if (import.meta.server) {
    const response = await useServerFetch<NewsDetailWrappedResponse<T>>(requestConfig)
    return response?.data ?? null
  }

  return await request<unknown, T>(requestConfig)
}
